/**
 * The packed boundary table, loaded on demand.
 *
 * At module scope it did not stay on the routes that search. `SearchCard` lives
 * in `app/_components`, so the table landed in a shared client chunk that 17 of
 * the 18 prerendered routes pull in — `/terms` and `/privacy` among them — for
 * ~32 KB gzipped of boundaries none of them can use. Behind `import()` it
 * becomes its own chunk that only a route executing `loadTimeZoneTable` fetches.
 *
 * Deferring it is safe because no coordinate exists until Mapbox resolves a
 * place, and the search forms start this load when they mount, so the table is
 * in memory long before the round trip that produces a lat/lng returns. Until it
 * is, `timeZoneForCoords` answers `undefined` — the same "fall back to the
 * viewer's zone" every caller already handles for an unplaceable coordinate.
 */
type TzLookup = (lat: number, lng: number) => string;

let table: TzLookup | null = null;
let pending: Promise<void> | null = null;
const arrivals = new Set<() => void>();

/**
 * Starts the load, or joins one in flight. Idempotent, so every consumer can
 * call it on mount without coordinating. A failed fetch resets `pending` so a
 * later attempt can retry rather than latching the fallback forever; callers see
 * `undefined` in the meantime and degrade to the viewer's zone.
 */
export function loadTimeZoneTable(): Promise<void> {
  if (table) return Promise.resolve();
  pending ??= import("@photostructure/tz-lookup")
    .then((m) => {
      table = m.default;
      for (const notify of arrivals) notify();
    })
    .catch(() => {
      pending = null;
    });
  return pending;
}

/** Whether the table can answer yet. For `useSyncExternalStore` snapshots. */
export function isTimeZoneTableLoaded(): boolean {
  return table !== null;
}

/** Notified once, when the table lands, so a render can pick up the real zone. */
export function subscribeToTimeZoneTable(notify: () => void): () => void {
  arrivals.add(notify);
  return () => {
    arrivals.delete(notify);
  };
}

/**
 * Whether `Intl` accepts a zone. Cached per zone — a search session touches one
 * or two, so this is a couple of probes rather than one per render.
 */
const usableTimeZones = new Map<string, boolean>();

export function isUsableTimeZone(timeZone: string): boolean {
  const cached = usableTimeZones.get(timeZone);
  if (cached !== undefined) return cached;

  let usable: boolean;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    usable = true;
  } catch {
    usable = false;
  }
  usableTimeZones.set(timeZone, usable);
  return usable;
}

/**
 * The IANA zone a coordinate sits in — the *destination's* clock, for the part
 * of the flow that runs before any spot exists.
 *
 * Search picks a start and end time against a destination the driver typed, and
 * the spot (with its own `timezone`) only comes back in the response those
 * times are an input to. So there is nothing to read off `spot_details` yet, and
 * without this the window is built on the browser's clock: a driver in
 * California picking 8:00 AM for a New York trip searches the 11:00 AM window —
 * and is offered 8:00 AM at all, even though it is already past at the
 * destination.
 *
 * Resolved locally from a packed boundary table rather than a request: it runs
 * on every destination change and gates the picker's bounds, and a picker that
 * can't open until a round trip returns is worse than one that opens on the
 * wrong clock. Mapbox returns no zone of its own, and we expose no endpoint for
 * it.
 *
 * Returns `undefined` for a coordinate the lookup rejects, a zone `Intl` won't
 * take, or a table that hasn't arrived yet — which every caller here treats as
 * "fall back to the viewer's zone", the behaviour these screens had before. A
 * spot's own `timezone` stays authoritative once one exists; this covers only the
 * window before that.
 *
 * Synchronous, so it can be read during render: it reports what the table can
 * answer *now*. React callers should use `useDestinationTimeZone`, which starts
 * the load and re-renders when it lands; anything else must call
 * `loadTimeZoneTable` first if it needs a definite answer.
 */
export function timeZoneForCoords(
  lat: number | undefined | null,
  lng: number | undefined | null,
): string | undefined {
  if (table === null) return undefined;
  if (typeof lat !== "number" || typeof lng !== "number") return undefined;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;

  let zone: string;
  try {
    zone = table(lat, lng);
  } catch {
    // Throws on out-of-range coordinates.
    return undefined;
  }
  return isUsableTimeZone(zone) ? zone : undefined;
}
